import get from 'lodash/get';

import { BadgeColor } from '@/hooks/useBadgeClasses';

import ClockIcon from '@/icons/line/clock.svg';
import EditAlt from '@/icons/line/edit-alt.svg';

type DataRequestLike =
  | {
      status?: { value?: string | null } | null;
      attributes?: Record<string, unknown> | null;
    }
  | null
  | undefined;

export function useDataRequestIndicators(options: {
  dataRequest?: DataRequestLike;
  base?: Record<string, unknown> | null;
}) {
  const getComparable = (value: unknown) => {
    if (
      typeof value === 'object' &&
      value !== null &&
      'value' in (value as Record<string, unknown>)
    ) {
      return (value as { value?: unknown }).value;
    }
    if (typeof value === 'object' && value !== null && 'id' in (value as Record<string, unknown>)) {
      return (value as { id?: unknown }).id;
    }
    if (value === '') {
      return undefined;
    }
    return value;
  };

  function hasAttributeChange(
    baseKey: string,
    attrKeyOverride?: string,
    baseValueOverride?: unknown
  ): boolean {
    const attributes = options.dataRequest?.attributes as Record<string, unknown> | undefined;
    if (!attributes) {
      return false;
    }
    const attrKey = attrKeyOverride ?? baseKey;
    let rawAttr = get(attributes, attrKey);
    if (rawAttr === null || rawAttr === undefined || rawAttr === '') {
      rawAttr = get(attributes, baseKey);
    }
    if (rawAttr === null || rawAttr === undefined || rawAttr === '') {
      return false;
    }
    const attrValue = getComparable(rawAttr);
    const baseRaw =
      baseValueOverride !== undefined ? baseValueOverride : get(options.base as unknown, baseKey);
    const baseValue = getComparable(baseRaw);
    return attrValue !== baseValue;
  }

  const computedStatusBadge = computed(() => {
    if (!options.base) {
      return {
        label: undefined,
        color: undefined,
        icon: undefined
      } as const;
    }
    const status = options.dataRequest?.status?.value as string | undefined;
    switch (status) {
      case 'rejected':
        return {
          label: 'Edited',
          color: BadgeColor.secondary,
          icon: EditAlt
        } as const;
      case 'pending':
        return {
          label: 'Pending',
          color: BadgeColor.warning,
          icon: ClockIcon
        } as const;
      default:
        return {
          label: undefined,
          color: undefined,
          icon: undefined
        } as const;
    }
  });

  return { computedStatusBadge, hasAttributeChange };
}
